const PROVISIONAL_SLUG_MAX_LENGTH = 63;
const DEFAULT_SITE_SLUG_BASE = 'mi-sitio-inmobiliario';

/**
 * Converts user-provided text into a valid provisional website slug.
 *
 * @param value - Text to normalize.
 * @param maxLength - Maximum length of the normalized result.
 * @returns A lowercase ASCII slug without boundary hyphens.
 */
export const normalizeProvisionalSlug = (
  value: string,
  maxLength = PROVISIONAL_SLUG_MAX_LENGTH
): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, maxLength)
    .replace(/-+$/g, '');

/**
 * Builds the initial globally unique provisional slug for a company website.
 *
 * @param siteName - Company name or fallback site name.
 * @param companyId - Stable globally unique company identifier.
 * @returns A valid slug that preserves the unique ID suffix.
 */
export const buildDefaultProvisionalSlug = (
  siteName: string,
  companyId: number
): string => {
  const uniqueSuffix = String(companyId);
  const maximumBaseLength =
    PROVISIONAL_SLUG_MAX_LENGTH - uniqueSuffix.length - 1;
  const normalizedBase = normalizeProvisionalSlug(
    siteName || DEFAULT_SITE_SLUG_BASE,
    maximumBaseLength
  );
  const base = normalizedBase || DEFAULT_SITE_SLUG_BASE;

  return `${base}-${uniqueSuffix}`;
};
